// src/components/borrowing/ListDefaulter.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { reportService } from '../../services/reportService';
import { fileStorageService } from '../../services/fileStorageService';
import { generateShareLinks } from '../../services/notificationService';
import { ReportStatus } from '../constants';
import MessageSenderModal from '../messaging/MessageSenderModal';
import { AlertCircle, ArrowLeft, Shield, Upload, X, FileText, File, Lock, Users, Plus, Send, Mail, MessageCircle, Loader2 } from 'lucide-react';

const MAX_DAILY_POSTS = 3;

const ListDefaulter = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [agreed, setAgreed] = useState(false);
  
  // Submission Success State
  const [submittedReport, setSubmittedReport] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  // File Upload State
  const [attachments, setAttachments] = useState([]);
  
  // Co-Borrowers State
  const [coBorrowers, setCoBorrowers] = useState([]);
  const [newCoBorrower, setNewCoBorrower] = useState({ name: '', mobile: '', email: '' });
  const [showCoBorrowerForm, setShowCoBorrowerForm] = useState(false);
  
  // Edit Mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingReportId, setExistingReportId] = useState(null);
  const [existingReport, setExistingReport] = useState(null);

  // Stats
  const [dailyPostCount, setDailyPostCount] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    amount: '',
    agreementDate: '',
    dueDate: '',
    description: '',
  });

  useEffect(() => {
    if (!currentUser || !userProfile) {
      navigate('/auth');
      return;
    }

    if (!userProfile.roles?.includes('LENDER')) {
      navigate('/dashboard');
      return;
    }

    loadDailyStats();
    checkEditMode();
  }, [currentUser, userProfile, navigate]);

  const loadDailyStats = async () => {
    if (!currentUser) return;
    try {
      const count = await reportService.getDailyPostCount(currentUser.uid);
      setDailyPostCount(count);
    } catch (error) {
      console.error('Error loading daily stats:', error);
    }
  };

  const checkEditMode = async () => {
    const pathParts = window.location.hash.split('/');
    if (pathParts[1] === 'edit' && pathParts[2]) {
      setIsEditMode(true);
      setExistingReportId(pathParts[2]);
      await loadExistingReport(pathParts[2]);
    }
  };

  const loadExistingReport = async (reportId) => {
    try {
      setLoading(true);
      const report = await reportService.getReport(reportId);
      
      if (!report || report.lenderId !== currentUser.uid) {
        navigate('/dashboard');
        return;
      }

      setExistingReport(report);
      setFormData({
        name: report.borrowerName,
        email: report.borrowerEmail || '',
        mobile: report.borrowerMobile,
        amount: report.amount.toString(),
        agreementDate: report.agreementDate,
        dueDate: report.dueDate,
        description: report.description,
      });
      
      if (report.additionalBorrowers) {
        setCoBorrowers(report.additionalBorrowers);
      }
      
      if (report.evidenceAttachments) {
        setAttachments(report.evidenceAttachments);
      }
      
      setAgreed(true);
    } catch (error) {
      console.error('Error loading report:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) { 
        alert("File size exceeds 5MB limit.");
        return;
      }

      setUploadingFiles(true);
      try {
        // Create temporary preview
        const previewData = await fileStorageService.fileToBase64(file);
        const tempItem = {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          type: file.type,
          data: previewData,
          file: file, // Store file object for later upload
          isTemp: true
        };
        setAttachments([...attachments, tempItem]);
      } catch (error) {
        console.error('Error processing file:', error);
        alert('Failed to process file. Please try again.');
      } finally {
        setUploadingFiles(false);
      }
    }
  };

  const removeAttachment = (id) => {
    setAttachments(attachments.filter(a => a.id !== id));
  };

  const addCoBorrower = () => {
    if (newCoBorrower.name && newCoBorrower.mobile) {
      setCoBorrowers([...coBorrowers, { ...newCoBorrower }]);
      setNewCoBorrower({ name: '', mobile: '', email: '' });
      setShowCoBorrowerForm(false);
    }
  };

  const removeCoBorrower = (index) => {
    const updated = [...coBorrowers];
    updated.splice(index, 1);
    setCoBorrowers(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      alert("You must agree to the terms to submit a report.");
      return;
    }

    setLoading(true);

    try {
      if (!currentUser || !userProfile) {
        navigate('/auth');
        return;
      }

      // Check daily limit for unverified users
      if (!isEditMode && !userProfile.isVerified) {
        if (dailyPostCount >= MAX_DAILY_POSTS) {
          alert(`Daily limit reached. Unverified lenders can only post ${MAX_DAILY_POSTS} reports per day.`);
          navigate('/profile');
          return;
        }
      }

      // Upload new files to Firebase Storage
      const uploadedFiles = [];
      const reportId = existingReportId || Math.random().toString(36).substr(2, 9);

      for (const attachment of attachments) {
        if (attachment.isTemp && attachment.file) {
          // Upload new file
          const result = await fileStorageService.uploadLenderEvidence(reportId, attachment.file);
          uploadedFiles.push({
            id: attachment.id,
            name: attachment.name,
            type: attachment.type,
            url: result.url,
            path: result.path
          });
        } else {
          // Keep existing file
          uploadedFiles.push(attachment);
        }
      }

      const reportData = {
        lenderId: currentUser.uid,
        lenderName: userProfile.name,
        borrowerName: formData.name,
        borrowerEmail: formData.email,
        borrowerMobile: formData.mobile,
        additionalBorrowers: coBorrowers,
        amount: parseFloat(formData.amount),
        agreementDate: formData.agreementDate,
        dueDate: formData.dueDate,
        description: formData.description,
        status: ReportStatus.PENDING_RESPONSE,
        createdDate: existingReport?.createdDate || new Date().toISOString(),
        evidenceProvided: uploadedFiles.length > 0,
        evidenceAttachments: uploadedFiles,
      };

      let result;
      if (isEditMode) {
        await reportService.updateReport(reportId, reportData);
        result = { id: reportId, data: { ...reportData, id: reportId } };
      } else {
        result = await reportService.createReport(reportData);
      }

      setSubmittedReport(result.data);
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Post-submission notification screen
  if (submittedReport) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-slate-800 rounded-2xl p-8 border border-emerald-500/50 shadow-2xl animate-in slide-in-from-bottom-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="text-emerald-500 h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {isEditMode ? 'Report Updated Successfully' : 'Report Created Successfully'}
            </h2>
            <p className="text-slate-400 mt-2">
              The report is currently <strong className="text-yellow-400">Hidden</strong>. 
              Notify the borrower to start the 7-day grace period.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
              <p className="text-xs text-slate-400 uppercase">Borrower</p>
              <p className="text-white font-bold">{submittedReport.borrowerName}</p>
              <p className="text-slate-400 text-sm font-mono">{submittedReport.borrowerMobile}</p>
            </div>
            <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
              <p className="text-xs text-slate-400 uppercase">Amount</p>
              <p className="text-emerald-400 font-bold text-xl font-mono">
                ${submittedReport.amount?.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => setShowMessageModal(true)}
              className="w-full flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-emerald-900/20"
            >
              <Send size={20} />
              Send Notification to Borrower
            </button>
            
            <div className="flex gap-3">
              <button
                onClick={() => window.open(generateShareLinks(submittedReport).waLink, '_blank')}
                className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-3 rounded-xl transition-all"
              >
                <MessageCircle size={18} />
                Quick WhatsApp
              </button>
              {submittedReport.borrowerEmail && (
                <a
                  href={generateShareLinks(submittedReport).mailLink}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl transition-all"
                >
                  <Mail size={18} />
                  Quick Email
                </a>
              )}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-700">
            <button 
              onClick={() => navigate('/dashboard')}
              className="w-full text-slate-400 hover:text-white text-sm font-medium transition-colors py-2"
            >
              Skip & Go to Dashboard →
            </button>
          </div>
        </div>

        {/* Message Modal */}
        <MessageSenderModal
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          report={submittedReport}
          onMessageSent={() => {
            setShowMessageModal(false);
          }}
        />
      </div>
    );
  }

  // Main form
  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <button 
        onClick={() => navigate('/dashboard')}
        className="flex items-center text-slate-400 hover:text-white mb-6 transition-colors group"
      >
        <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" /> 
        Back to Dashboard
      </button>

      <div className="bg-slate-800 rounded-2xl p-8 shadow-2xl border border-slate-700">
        <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Shield className="text-emerald-500 h-8 w-8" />
            <h1 className="text-3xl font-bold text-white">
              {isEditMode ? 'Edit Credit Report' : 'Submit a Credit Report'}
            </h1>
          </div>
          {!userProfile?.isVerified && !isEditMode && (
            <div className="flex flex-col items-end">
              <span className="text-xs text-slate-400 uppercase font-bold mb-1">Daily Limit</span>
              <span className="text-xs bg-slate-900 px-2 py-1 rounded border border-slate-700 text-slate-300">
                {dailyPostCount} / {MAX_DAILY_POSTS} Used
              </span>
              <Link to="/profile" className="text-[10px] text-emerald-500 hover:underline mt-1">
                Increase Limit
              </Link>
            </div>
          )}
        </div>
        
        <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg mb-8">
          <h4 className="text-blue-400 font-bold mb-2 flex items-center gap-2">
            <AlertCircle size={16}/> How it works
          </h4>
          <ul className="text-sm text-slate-300 space-y-1 list-disc list-inside">
            <li>Submit the details of the debt below.</li>
            <li>We generate a <strong>"PLEASE PAY ME"</strong> message link for you.</li>
            <li>You send it to the borrower via WhatsApp or Email.</li>
            <li>If not resolved in <strong>7 days</strong>, the report becomes public.</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Borrower Name (Primary)
              </label>
              <input 
                required
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2 flex justify-between">
                <span>Borrower Mobile (Required)</span>
                {isEditMode && (
                  <span className="text-xs text-orange-400 flex items-center gap-1">
                    <Lock size={10}/> Locked
                  </span>
                )}
              </label>
              <input 
                required
                type="tel" 
                name="mobile"
                disabled={isEditMode}
                placeholder="+1 (555) 000-0000"
                value={formData.mobile}
                onChange={handleChange}
                className={`w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all ${
                  isEditMode ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              />
            </div>
          </div>

          {/* Co-Borrowers Section */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Users size={16} className="text-blue-400"/> Additional Borrowers (Optional)
              </label>
              {!showCoBorrowerForm && (
                <button 
                  type="button"
                  onClick={() => setShowCoBorrowerForm(true)}
                  className="text-xs flex items-center gap-1 text-emerald-500 hover:text-emerald-400 font-bold transition-colors"
                >
                  <Plus size={14}/> Add Co-Borrower
                </button>
              )}
            </div>

            {coBorrowers.length > 0 && (
              <div className="space-y-2 mb-4">
                {coBorrowers.map((cb, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-slate-800 p-2 rounded border border-slate-700">
                    <div>
                      <p className="text-sm text-white font-medium">{cb.name}</p>
                      <p className="text-xs text-slate-500">
                        {cb.mobile} {cb.email && `• ${cb.email}`}
                      </p>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeCoBorrower(idx)} 
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showCoBorrowerForm && (
              <div className="bg-slate-800 p-3 rounded border border-slate-600 space-y-3 animate-in slide-in-from-top-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input 
                    type="text"
                    placeholder="Co-Borrower Name"
                    value={newCoBorrower.name}
                    onChange={(e) => setNewCoBorrower({...newCoBorrower, name: e.target.value})}
                    className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:ring-1 focus:ring-emerald-500 outline-none"
                  />
                  <input 
                    type="tel"
                    placeholder="Mobile Number"
                    value={newCoBorrower.mobile}
                    onChange={(e) => setNewCoBorrower({...newCoBorrower, mobile: e.target.value})}
                    className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:ring-1 focus:ring-emerald-500 outline-none"
                  />
                  <input 
                    type="email"
                    placeholder="Email (Optional)"
                    value={newCoBorrower.email}
                    onChange={(e) => setNewCoBorrower({...newCoBorrower, email: e.target.value})}
                    className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:ring-1 focus:ring-emerald-500 outline-none md:col-span-2"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button 
                    type="button" 
                    onClick={() => setShowCoBorrowerForm(false)}
                    className="text-xs text-slate-400 hover:text-white px-3 py-1 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    onClick={addCoBorrower}
                    className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1 rounded transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Amount ($)</label>
              <input 
                required
                type="number" 
                name="amount"
                min="1"
                step="0.01"
                value={formData.amount}
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                placeholder="1000.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email (Optional)
              </label>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                placeholder="borrower@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Agreement Date
              </label>
              <input 
                required
                type="date" 
                name="agreementDate"
                value={formData.agreementDate}
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Due Date</label>
              <input 
                required
                type="date" 
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Message to Borrower / Description
            </label>
            <textarea 
              required
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleChange}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none resize-none transition-all"
              placeholder="E.g., You promised to pay back the loan for the car repair last Friday..."
            ></textarea>
            <p className="text-xs text-slate-500 mt-2">
              This text will be included in the 'PLEASE PAY ME' message sent to the borrower.
            </p>
          </div>

          {/* Evidence Upload Section */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Evidence Attachments (Optional)
            </label>
            <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 hover:bg-slate-700/30 transition-colors">
              <input 
                type="file" 
                id="evidence-upload"
                className="hidden" 
                accept="image/*,.pdf"
                onChange={handleFileChange}
                disabled={uploadingFiles}
              />
              <label 
                htmlFor="evidence-upload" 
                className={`cursor-pointer flex flex-col items-center justify-center gap-2 ${
                  uploadingFiles ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {uploadingFiles ? (
                  <>
                    <Loader2 className="text-emerald-500 animate-spin" size={24} />
                    <span className="text-sm text-slate-400 font-medium">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="text-emerald-500" size={24} />
                    <span className="text-sm text-slate-400 font-medium">Click to upload evidence</span>
                    <span className="text-xs text-slate-600">Max size 5MB. Images or PDF.</span>
                  </>
                )}
              </label>
            </div>

            {attachments.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {attachments.map((file) => (
                  <div key={file.id} className="relative group bg-slate-900 rounded-lg border border-slate-700 p-2 hover:border-slate-600 transition-colors">
                    <button
                      type="button"
                      onClick={() => removeAttachment(file.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600 transition-colors z-10"
                    >
                      <X size={12} />
                    </button>
                    <div className="h-24 w-full rounded overflow-hidden flex items-center justify-center bg-black/50">
                      {file.type.startsWith('image/') ? (
                        <img 
                          src={file.url || file.data} 
                          alt={file.name} 
                          className="h-full w-full object-cover" 
                        />
                      ) : file.type === 'application/pdf' ? (
                        <div className="flex flex-col items-center">
                          <FileText className="text-red-400 mb-1" size={32} />
                          <span className="text-[10px] text-slate-500 uppercase font-bold">PDF</span>
                        </div>
                      ) : (
                        <File className="text-slate-400" size={32} />
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-2 truncate px-1">{file.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-700">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input 
                type="checkbox" 
                checked={agreed} 
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-5 h-5 accent-emerald-500 bg-slate-900 border-slate-600 rounded cursor-pointer"
              />
              <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                I certify that this information is accurate. I understand that if the borrower does not respond within 7 days, this report will become public.
              </span>
            </label>
          </div>

          <button 
            type="submit" 
            disabled={loading || !agreed || uploadingFiles}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 rounded-lg shadow-lg shadow-emerald-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Processing...
              </>
            ) : (
              isEditMode ? 'Update Report' : 'Create & Notify Borrower'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ListDefaulter;